import { useEffect, useState } from 'react'

/** Muss zur Dauer der `nav-peek-out`-Keyframes in MainLayout.scss passen. */
export const PEEK_EXIT_MS = 350

/**
 * Wie der Peek endet. Zwei Ausgänge, die gegensätzlich behandelt werden müssen:
 *
 * - `'leaving'` — der Zeiger ist weg, die Navigation bleibt ausgeblendet. Die
 *   Leisten fahren als Overlay aus und müssen dafür ihre absolute Positionierung
 *   behalten, bis ihre Breite bei 0 ist.
 * - `'docking'` — der Klick aufs Titlebar-Icon setzt den Peek fest. Die Leisten
 *   stehen schon an ihrem endgültigen Platz, es ändert sich nur der Fluss. Hier
 *   darf gar nichts animieren.
 */
export type PeekExitPhase = 'leaving' | 'docking' | null

/**
 * Hält den Peek für die Dauer seines Ausgangs in einer Phase — analog zu
 * `listExiting` / `fullPhase` beim Fenstermodus.
 *
 * Grund: der Peek zeigt die Leisten als Overlay, indem er sie absolut
 * positioniert und ihre Breite auf 48px/240px setzt. Fiele die Peek-Klasse
 * ersatzlos weg, geschähe zweierlei in EINEM Frame — die Leisten kämen zurück
 * in den Flex-Fluss (`position` springt, das animiert nicht), und ihre Breite
 * begänne ihre 200ms von 240px auf 0 zu laufen. Der Inhalt würde um 288px nach
 * rechts geschoben und rutschte sichtbar zurück, samt Neuberechnung der
 * Tab-Silhouette per ResizeObserver. Genau das, was ein Overlay nie auslösen darf.
 *
 * Beim Festsetzen (`'docking'`) gilt das Gegenteil: dort MUSS der Fluss sofort
 * greifen. Bliebe die absolute Positionierung 200ms erhalten, läge der Inhalt so
 * lange in voller Breite unter dem Panel und rückte erst danach beiseite.
 *
 * WICHTIG — die Phase wird WÄHREND DES RENDERS abgeleitet, nicht in einem
 * Effekt. Ein `useEffect` läuft erst nach dem Malen: es gäbe also genau einen
 * sichtbaren Frame, in dem die Peek-Klasse schon weg und die Phasenklasse noch
 * nicht da ist — der Fehler wäre nur um einen Frame verschoben. Das Setzen von
 * State im Render ist hier Absicht: React verwirft den laufenden Durchlauf und
 * rendert sofort neu, noch vor dem Commit, sodass beide Klassenwechsel in
 * DERSELBEN DOM-Änderung landen.
 */
export function usePeekExit(isPeekActive: boolean, navigationVisible: boolean): PeekExitPhase {
  const [isExiting, setIsExiting] = useState(false)
  const [wasActive, setWasActive] = useState(isPeekActive)

  if (wasActive !== isPeekActive) {
    setWasActive(isPeekActive)
    // Nur der Übergang offen → zu startet die Phase. Kehrt der Zeiger zurück,
    // beendet derselbe Zweig sie wieder.
    setIsExiting(wasActive && !isPeekActive)
  }

  useEffect(() => {
    if (!isExiting) return
    const timer = setTimeout(() => setIsExiting(false), PEEK_EXIT_MS)
    return () => clearTimeout(timer)
  }, [isExiting])

  if (!isExiting) return null
  // Bewusst aus dem AKTUELLEN Wert abgeleitet, nicht beim Start eingefroren:
  // wird die Navigation mitten in der Phase eingeblendet, gilt sofort 'docking'.
  return navigationVisible ? 'docking' : 'leaving'
}
